import { createPortal } from 'react-dom';
import { Manager, Reference, Popper, PopperChildrenProps } from 'react-popper';
import React, {
  FocusEventHandler,
  MouseEvent,
  MouseEventHandler,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import useOnClickOutside from 'hooks/useOnClickOutside';
import { PopoverTriggerBy, PopoverBaseProps } from './types';
import { Modifiers } from 'popper.js';

type OwnProps = {
  childrenRenderFunc: (props: PopperChildrenProps) => React.ReactNode;
  target: React.ReactElement;
};

type ComponentProps = OwnProps & PopoverBaseProps;

type TriggerEvents = {
  onBlur?: FocusEventHandler;
  onClick?: MouseEventHandler;
  onFocus?: FocusEventHandler;
};

const useControlled = ({
  show,
  onShowChange,
}: Pick<PopoverBaseProps, 'show' | 'onShowChange'>): [
  boolean,
  React.Dispatch<SetStateAction<boolean>>,
] => {
  const [open, setOpen] = useState<boolean>(show as boolean);

  useEffect(() => {
    setOpen(show as boolean);
  }, [show]);

  const isControlled = typeof onShowChange !== 'undefined';
  if (isControlled) {
    return [show as boolean, onShowChange as typeof setOpen];
  } else {
    return [open, setOpen];
  }
};

const PopoverBase = ({
  childrenRenderFunc,
  disabled = false,
  modifier,
  onShowChange,
  onDismiss,
  placement = 'bottom',
  rootClose = true,
  show = false,
  target,
  trigger = 'click',
  usePortal = true,
}: ComponentProps) => {
  const popperRef = useRef<HTMLElement>(null);
  const targetRef = useRef<HTMLElement>(null);

  // we check is our Popover controllable or uncontrolled
  const [open, setOpen] = useControlled({ show, onShowChange });

  const openItCallback = useCallback(() => setOpen(true), [setOpen]);
  const closeItCallback = useCallback(() => {
    setOpen(false);
    onDismiss?.();
  }, [setOpen]);

  const handleClickCallback = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setOpen(!open);
      if (open) onDismiss?.();
    },
    [setOpen, open]
  );

  useEffect(() => {
    const handleMouseMove = (e: Event) => {
      if (
        targetRef.current &&
        targetRef.current.contains(e.target as Element)
      ) {
        setOpen(true);
        return;
      }
      if (
        popperRef.current &&
        popperRef.current.contains(e.target as Element)
      ) {
        return;
      }
      setOpen(false);
      onDismiss?.();
    };

    if (trigger === 'hover') {
      window.addEventListener('mousemove', handleMouseMove);
    }
    return () => {
      if (trigger === 'hover') {
        window.removeEventListener('mousemove', handleMouseMove);
      }
    };
  }, [open, trigger, popperRef, targetRef, closeItCallback, setOpen]);

  const getTriggerFunctions: {
    [trigger in Exclude<PopoverTriggerBy, 'hover'>]: TriggerEvents;
  } = {
    click: {
      onClick: handleClickCallback,
    },
    focus: {
      onBlur: closeItCallback,
      onFocus: openItCallback,
    },
  };

  const triggerEventProps = useCallback(getTriggerFunctions[trigger], [
    trigger,
  ]);

  useOnClickOutside(popperRef, closeItCallback, open && rootClose, trigger);

  const scheduleUpdateRef = useRef<(() => void) | null>(null);
  const positionUpdateTimerRef = useRef<number | null>(null);
  const [positionReady, setPositionReady] = useState(false);

  const isPopperAnchored = useCallback((): boolean => {
    const popperEl = popperRef.current;
    const targetEl = targetRef.current;
    if (!popperEl || !targetEl) {
      return false;
    }

    const popperRect = popperEl.getBoundingClientRect();
    const targetRect = targetEl.getBoundingClientRect();
    if (popperRect.width === 0 || popperRect.height === 0) {
      return false;
    }

    // Popper renders at the document origin before its first update.
    if (popperRect.top <= 0 && popperRect.left <= 0) {
      return false;
    }

    const horizontalOverlap =
      popperRect.left < targetRect.right + 40 &&
      popperRect.right > targetRect.left - 40;
    const verticalDistance = Math.min(
      Math.abs(popperRect.top - targetRect.bottom),
      Math.abs(popperRect.bottom - targetRect.top)
    );

    return horizontalOverlap && verticalDistance < 400;
  }, []);

  const queuePositionUpdate = useCallback(() => {
    if (positionUpdateTimerRef.current !== null) {
      return;
    }
    positionUpdateTimerRef.current = window.setTimeout(() => {
      positionUpdateTimerRef.current = null;
      scheduleUpdateRef.current?.();
    }, 32);
  }, []);

  useLayoutEffect(() => {
    if (!usePortal || !open) {
      return;
    }

    popperRef.current?.setAttribute('data-apopover-portal', '');

    return () => {
      popperRef.current?.removeAttribute('data-apopover-portal');
    };
  }, [usePortal, open]);

  useLayoutEffect(() => {
    if (!open) {
      setPositionReady(false);
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const waitForAnchor = () => {
      if (cancelled) {
        return;
      }

      scheduleUpdateRef.current?.();
      attempts += 1;

      window.requestAnimationFrame(() => {
        if (cancelled) {
          return;
        }
        if (isPopperAnchored() || attempts >= 12) {
          setPositionReady(true);
          return;
        }
        waitForAnchor();
      });
    };

    waitForAnchor();

    return () => {
      cancelled = true;
    };
  }, [open, isPopperAnchored]);

  useEffect(() => {
    if (!open) {
      return;
    }

    window.addEventListener('scroll', queuePositionUpdate, true);
    window.addEventListener('resize', queuePositionUpdate);

    return () => {
      window.removeEventListener('scroll', queuePositionUpdate, true);
      window.removeEventListener('resize', queuePositionUpdate);
      if (positionUpdateTimerRef.current !== null) {
        window.clearTimeout(positionUpdateTimerRef.current);
        positionUpdateTimerRef.current = null;
      }
    };
  }, [open, queuePositionUpdate]);

  const shouldRenderPopper = open && (
    <Popper
      innerRef={popperRef}
      modifiers={modifier as Modifiers}
      placement={placement}
    >
      {(popperProps) => {
        scheduleUpdateRef.current = popperProps.scheduleUpdate ?? null;
        return childrenRenderFunc({
          ...popperProps,
          style: {
            ...popperProps.style,
            opacity: positionReady ? 1 : 0,
            visibility: positionReady ? 'visible' : 'hidden',
            pointerEvents: positionReady ? 'auto' : 'none',
          },
        });
      }}
    </Popper>
  );
  if (disabled) {
    return React.cloneElement(target);
  }

  return (
    <Manager>
      <Reference innerRef={targetRef}>
        {({ ref }) =>
          React.cloneElement(target, {
            ref,
            forwardRef: ref,
            ...triggerEventProps,
          })
        }
      </Reference>
      {usePortal
        ? createPortal(shouldRenderPopper, document.body)
        : shouldRenderPopper}
    </Manager>
  );
};

export default PopoverBase;
